import clsx from "clsx";
import FieldLabel from "../FieldLabel/FieldLabel";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { motion, useReducedMotion } from "framer-motion";
import { CSSProperties, memo } from "react";
import { ICON_SIZES } from "src/constants/icons";

export type CheckBoxType = {
  onChange: (isChecked: boolean) => void;
  checked: boolean;
  label?: string;
  description?: string;
  disabled?: boolean;
  labelClass?: string;
  iconClass?: string;
  iconStyles?: CSSProperties;
};

const CheckBox: React.FC<CheckBoxType> = ({
  onChange,
  checked,
  label,
  description,
  disabled = false,
  labelClass = "",
  iconClass = "",
  iconStyles,
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div
      className={clsx([
        "flex",
        "flex-col",
        "gap-0",
        { "pointer-events-none opacity-20": disabled },
      ])}
      onClick={() => !disabled && onChange(!checked)}
    >
      <div
        className={clsx([
          "flex",
          "flex-row",
          "w-full",
          "gap-1",
          "justify-start",
          "cursor-pointer",
          "max-w-full",
          {
            "items-start": description,
            "items-center": !description,
          },
        ])}
      >
        <motion.div
          key={checked ? "checked" : "unchecked"}
          {...(!prefersReducedMotion && {
            initial: { scale: checked ? 0.8 : 1, opacity: checked ? 0 : 1 },
            animate: { scale: 1, opacity: 1 },
            exit: { scale: 0.8, opacity: 0 },
            transition: { duration: 0.2, ease: "easeInOut" },
          })}
        >
          {checked ? (
            <MdCheckBox
              size={ICON_SIZES.MD}
              className={clsx([
                "cursor-pointer",
                "shrink-0",
                "text-emerald-400",
                "md:text-emerald-400",
                "dark:text-cyan-400",
                iconClass,
              ])}
              style={iconStyles}
            />
          ) : (
            <MdCheckBoxOutlineBlank
              size={ICON_SIZES.MD}
              className={clsx([
                "cursor-pointer",
                "shrink-0",
                "text-emerald-400",
                "md:text-emerald-400",
                "dark:text-cyan-400",
                iconClass,
              ])}
              style={iconStyles}
            />
          )}
        </motion.div>
        {!!(label || description) && (
          <div
            className={clsx(["flex", "flex-col", "max-w-full", "truncates"])}
          >
            {!!label && (
              <FieldLabel
                text={label}
                className={clsx(["cursor-pointer", labelClass])}
              />
            )}
            {!!description && (
              <div
                className={clsx(["flex", "w-full", "text-xs", "md:text-xs"])}
              >
                {description}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CheckBox);
